import React, { useState } from 'react';
import { Plus, Home, KeyRound } from 'lucide-react';
import classNames from 'classnames';
import { ScenarioType } from '../../types/app';

interface AddScenarioButtonProps {
  onAdd: (scenarioType: ScenarioType) => void;
  disabled?: boolean;
  className?: string;
  variant?: 'default' | 'compact';
  shouldPulse?: boolean;
}

export const AddScenarioButton: React.FC<AddScenarioButtonProps> = ({
  onAdd,
  disabled = false,
  className = '',
  variant = 'default',
  shouldPulse = false,
}) => {
  const [showOptions, setShowOptions] = useState(false);

  // Add custom CSS for enhanced pulse animation
  React.useEffect(() => {
    if (!shouldPulse) return;

    const styleId = 'enhanced-pulse-style';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes enhanced-pulse {
        0% {
          opacity: 1;
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.46);
        }
        50% {
          opacity: 0.9;
          transform: scale(1.02);
          box-shadow: 0 0 0 5px rgba(34, 197, 94, 0.16);
        }
        100% {
          opacity: 1;
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.46);
        }
      }
      .enhanced-pulse {
        animation: enhanced-pulse 1.4s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }, [shouldPulse]);

  if (disabled) {
    return (
      <div className={classNames(
        'w-full p-4 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 cursor-not-allowed flex items-center justify-center space-x-2',
        className
      )}>
        <Plus className="w-5 h-5" />
        <span className="text-sm font-medium">Maximum scenarios reached</span>
      </div>
    );
  }

  if (!showOptions) {
    if (variant === 'compact') {
      return (
        <div className={classNames('relative inline-block', className)}>
          <button
            onClick={() => setShowOptions(true)}
            className={classNames(
              'px-3 py-1 rounded-md border text-xs cursor-pointer transition-all duration-200 flex items-center justify-center space-x-2',
              shouldPulse 
                ? 'border-green-500 text-green-700 bg-green-100 enhanced-pulse' 
                : 'border-green-300 text-green-700 hover:border-green-400 hover:bg-green-50'
            )}
          >
            <Plus className="w-3 h-3" />
            <span className="text-xs font-medium">New Scenario</span>
          </button>
        </div>
      );
    }
    return (
      <button
        onClick={() => setShowOptions(true)}
        className={classNames(
          'w-full p-4 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 flex items-center justify-center space-x-2',
          shouldPulse 
            ? 'border-green-500 text-green-700 bg-green-100 enhanced-pulse' 
            : 'border-green-300 text-green-600 hover:border-green-400 hover:bg-green-50',
          className
        )}
      >
        <Plus className="w-5 h-5" />
        <span className="text-sm font-medium">New Scenario</span>
      </button>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={classNames('relative inline-block', className)}>
        <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-2 space-y-2 z-20">
          <button
            onClick={() => {
              onAdd('buying');
              setShowOptions(false);
            }}
            className="w-full p-2 rounded border border-green-300 text-green-700 hover:border-green-400 hover:bg-green-50 flex items-center space-x-2 text-sm"
          >
            <Home className="w-4 h-4" />
            <span>Buying Scenario</span>
          </button>
          <button
            onClick={() => {
              onAdd('renting');
              setShowOptions(false);
            }}
            className="w-full p-2 rounded border border-blue-300 text-blue-700 hover:border-blue-400 hover:bg-blue-50 flex items-center space-x-2 text-sm"
          >
            <KeyRound className="w-4 h-4" />
            <span>Renting Scenario</span>
          </button>
          <button
            onClick={() => setShowOptions(false)}
            className="w-full p-2 text-gray-500 hover:text-gray-700 text-xs"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('w-full space-y-2', className)}>
      <button
        onClick={() => {
          onAdd('buying');
          setShowOptions(false);
        }}
        className="w-full p-3 rounded-lg border-2 border-green-300 text-green-700 hover:border-green-400 hover:bg-green-50 cursor-pointer transition-all duration-200 flex items-center space-x-2"
      >
        <Home className="w-4 h-4" />
        <span className="text-sm font-medium">Buying Scenario</span>
      </button>
      <button
        onClick={() => {
          onAdd('renting');
          setShowOptions(false);
        }}
        className="w-full p-3 rounded-lg border-2 border-blue-300 text-blue-700 hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-all duration-200 flex items-center space-x-2"
      >
        <KeyRound className="w-4 h-4" />
        <span className="text-sm font-medium">Renting Scenario</span>
      </button>
      <button
        onClick={() => setShowOptions(false)}
        className="w-full p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200 text-sm"
      >
        Cancel
      </button>
    </div>
  );
};
